/* Added the following code for POW-NOTIFY.Istiyak using AI Agent. April 5, 2026. Code Start */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { API_URL, NOTIFICATIONS_WS_URL } from 'app/constants/ApiConstants';
import {
	fetchNotificationsList,
	markNotificationRead,
	markAllNotificationsRead,
	deleteNotificationById,
	deleteAllNotifications
} from 'app/services/notifications/notificationApi';
import { createNotificationsStompClient } from 'app/services/notifications/notificationStompClient';

/** Normalize list items; supports both legacy and current notification identifiers. */
function normalizeNotification(n) {
	if (!n || typeof n !== 'object') {
		return null;
	}
	const id = n.notificationId ?? n.id;
	return {
		...n,
		id,
		notificationId: n.notificationId ?? n.id,
		isRead: typeof n.isRead === 'boolean' ? n.isRead : false
	};
}

function sortLatestFirst(list) {
	return [...list].sort((a, b) => {
		const ta = new Date(a.createdAt || 0).getTime();
		const tb = new Date(b.createdAt || 0).getTime();
		return tb - ta;
	});
}

function mergeIncoming(prev, incoming) {
	const item = normalizeNotification(incoming);
	if (!item || item.id === undefined || item.id === null) {
		return prev;
	}
	const idx = prev.findIndex(x => String(x.notificationId ?? x.id) === String(item.notificationId ?? item.id));
	if (idx >= 0) {
		const next = [...prev];
		next[idx] = { ...next[idx], ...item };
		return sortLatestFirst(next);
	}
	return sortLatestFirst([item, ...prev]);
}

function mergeBatch(prev, incomingList) {
	if (!Array.isArray(incomingList) || incomingList.length === 0) {
		return prev;
	}
	const byId = new Map();
	prev.forEach(item => {
		const normalized = normalizeNotification(item);
		if (!normalized || normalized.id === undefined || normalized.id === null) {
			return;
		}
		byId.set(String(normalized.notificationId ?? normalized.id), normalized);
	});
	incomingList.forEach(item => {
		const normalized = normalizeNotification(item);
		if (!normalized || normalized.id === undefined || normalized.id === null) {
			return;
		}
		const key = String(normalized.notificationId ?? normalized.id);
		const existing = byId.get(key);
		byId.set(key, existing ? { ...existing, ...normalized } : normalized);
	});
	return sortLatestFirst(Array.from(byId.values()));
}

function getNotificationKey(item) {
	if (!item || typeof item !== 'object') {
		return null;
	}
	return item.notificationId ?? item.id ?? null;
}

/**
 * Loads user-specific notifications from REST, keeps them sorted by createdAt desc,
 * appends/updates from `/user/queue/notifications` over STOMP, and supports pagination.
 *
 * @param {boolean} enabled - When false, skips fetch and WebSocket.
 */
export function useNotifications(enabled = true) {
	const [notifications, setNotifications] = useState([]);
	const [loadError, setLoadError] = useState(null);
	const [wsState, setWsState] = useState('idle');
	const [currentPage, setCurrentPage] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [isFetchingPage, setIsFetchingPage] = useState(false);
	/* Added the following code for POW-938.Harsha using AI Agent. September 20, 2026. Code Start */
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);
	/* Code end. Harsha using AI Agent. September 20, 2026 */
	const isFetchingRef = useRef(false);

	const username = useMemo(() => {
		const uname =
			localStorage.getItem('UserName') ||
			localStorage.getItem('userName') ||
			localStorage.getItem('username');
		return uname ? String(uname).trim() : '';
	}, []);

	const unreadCount = useMemo(
		() => notifications.filter(n => n && n.isRead === false).length,
		[notifications]
	);

	const hasMore = useMemo(() => {
		if (totalPages <= 0) {
			return false;
		}
		return currentPage < totalPages - 1;
	}, [currentPage, totalPages]);

	const fetchPage = useCallback(
		async page => {
			if (!enabled || !username || isFetchingRef.current) {
				return;
			}
			isFetchingRef.current = true;
			setIsFetchingPage(true);
			setLoadError(null);
			try {
				const response = await fetchNotificationsList({ username, page, size: 50 });
				const content = Array.isArray(response?.content) ? response.content : [];
				setNotifications(prev => (page === 0 ? mergeBatch([], content) : mergeBatch(prev, content)));
				setCurrentPage(Number(response?.number) || page);
				setTotalPages(Number(response?.totalPages) || 0);
			} catch (e) {
				setLoadError(e);
			} finally {
				isFetchingRef.current = false;
				setIsFetchingPage(false);
				/* Added the following code for POW-938.Harsha using AI Agent. September 20, 2026. Code Start */
				if (page === 0) {
					setInitialLoadComplete(true);
				}
				/* Code end. Harsha using AI Agent. September 20, 2026 */
			}
		},
		[enabled, username]
	);

	const fetchNextPage = useCallback(() => {
		if (!enabled || !hasMore || isFetchingPage) {
			return;
		}
		fetchPage(currentPage + 1);
	}, [enabled, hasMore, isFetchingPage, fetchPage, currentPage]);

	const markOneReadLocal = useCallback(notificationId => {
		if (notificationId === undefined || notificationId === null) {
			return;
		}
		setNotifications(prev =>
			prev.map(item =>
				String(item.notificationId ?? item.id) === String(notificationId)
					? { ...item, isRead: true }
					: item
			)
		);
	}, []);

	/* Added the following code for Istiyak using AI Agent. April 8, 2026. Code Start */
	const markOneRead = useCallback(
		async notificationId => {
			if (notificationId === undefined || notificationId === null) {
				return;
			}
			const existing = notifications.find(
				item => String(item.notificationId ?? item.id) === String(notificationId)
			);
			if (existing && existing.isRead === true) {
				return;
			}
			markOneReadLocal(notificationId);
			if (!username) {
				return;
			}
			try {
				await markNotificationRead({ notificationsId: notificationId, username });
			} catch (e) {
				setLoadError(e);
			}
		},
		[notifications, markOneReadLocal, username]
	);

	const markAllRead = useCallback(async () => {
		setNotifications(prev => prev.map(item => ({ ...item, isRead: true })));
		if (!username) {
			return;
		}
		try {
			await markAllNotificationsRead({ username });
		} catch (e) {
			setLoadError(e);
		}
	}, [username]);

	const deleteOne = useCallback(
		async notificationId => {
			if (notificationId === undefined || notificationId === null) {
				return;
			}
			setNotifications(prev =>
				prev.filter(item => String(getNotificationKey(item)) !== String(notificationId))
			);
			if (!username) {
				return;
			}
			try {
				await deleteNotificationById({ notificationsId: notificationId, username });
			} catch (e) {
				setLoadError(e);
			}
		},
		[username]
	);

	const deleteAll = useCallback(async () => {
		setNotifications([]);
		setCurrentPage(0);
		setTotalPages(0);
		if (!username) {
			return;
		}
		try {
			await deleteAllNotifications({ username });
		} catch (e) {
			setLoadError(e);
		}
	}, [username]);
	/* Code end. Istiyak using AI Agent. April 8, 2026 */

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		let client;
		let cancelled = false;

		(async () => {
			setLoadError(null);
			await fetchPage(0);

			if (cancelled) {
				return;
			}

			/* Added the following code for POW-937.Harsha using AI Agent. September 20, 2026. Code Start */
			const notificationsBaseUrl = NOTIFICATIONS_WS_URL || API_URL;
			let brokerWsUrl;
			try {
				brokerWsUrl = `${new URL(notificationsBaseUrl).origin}/ws`;
			} catch (e) {
				brokerWsUrl = `${notificationsBaseUrl}/ws`;
			}
			/* Code end. Harsha using AI Agent. September 20, 2026 */
			client = createNotificationsStompClient({
				brokerWsUrl,
				/* Bearer on STOMP CONNECT; WebSocket handshake cannot carry custom headers in the browser. */
				getConnectHeaders: () => {
					const token = localStorage.getItem('bearer_token');
					return token ? { Authorization: `Bearer ${token}` } : {};
				},
				onConnectionState: state => {
					if (!cancelled) {
						setWsState(state);
					}
				},
				onNotification: n => {
					if (!cancelled) {
						setNotifications(prev => mergeIncoming(prev, n));
					}
				}
			});

			setWsState('connecting');
			client.activate();
		})();

		return () => {
			cancelled = true;
			if (client) {
				client.deactivate();
			}
			setWsState('idle');
		};
	}, [enabled, fetchPage, username]);

	return {
		notifications,
		unreadCount,
		loadError,
		wsState,
		currentPage,
		hasMore,
		isFetchingPage,
		initialLoadComplete,
		fetchNextPage,
		markOneReadLocal,
		markOneRead,
		markAllRead,
		deleteOne,
		deleteAll
	};
}
/* Code end. Istiyak using AI Agent. April 5, 2026 */
